/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import IScene from "../../scene_interface";
import {vec2, vec3, vec4} from "gl-matrix";
import {GLU, RenderDocument }from "js_renderer";

interface IPGC {
    windowPoint: vec4;
    msPositionForRoundedWindowPoint: vec3;
}

export default
class Annotation {

    private static id: number = 1;
    protected _sceneAPI: IScene;
    protected _renderDocument: any;
    private _cssScreenPosition: vec2;
    private _callback: any;
    private _position: vec3;
    private _uid: number;
    private _pgc: IPGC;

    constructor(aRenderDocument: RenderDocument, aScreenCssPositionX, aScreenCssPositionY, aCallback) {
        this._renderDocument = aRenderDocument;
        this._sceneAPI = aRenderDocument.getSceneAPI();
        this._cssScreenPosition = vec2.fromValues(aScreenCssPositionX, aScreenCssPositionY);
        this._callback = aCallback;
        this._position = this._sceneAPI.pickingModule.getPrecise3DPosition(aScreenCssPositionX, aScreenCssPositionY);
        this._uid = Annotation.id++;

        this._pgc = {
            windowPoint: vec4.create(),
            msPositionForRoundedWindowPoint: vec3.create()
        };

        this.updateScreenPositionAndOcclusion(this._sceneAPI.viewModeModule.getActiveView());
    }

    public getUID(): number {
        return this._uid;
    }

    public get3DPosition(): vec3 {
        return vec3.clone(this._position);
    }

    public set3DPosition(a3DPosition: vec3) {
        this._position = vec3.clone(a3DPosition);

        this.updateScreenPositionAndOcclusion(this._sceneAPI.viewModeModule.getActiveView());
    }

    public getScreenPosition(): vec2 {
        return vec2.fromValues(Math.round(this._cssScreenPosition[0]), Math.round(this._cssScreenPosition[1]));
    }

    /**
     * Recompute the screen position based on the current transformation in camera in current view.
     * @param {object} aView
     */
    public updateScreenPositionAndOcclusion(aView: any): boolean {
        let occluded = false;
        let visible = false;

        // transform the 3D position of this annotation to viewport
        let windowPoint = this._pgc.windowPoint;
        // export function project(aOut: vec4, aPoint: vec3, aModelProj: mat4, aView: number[]): void
        GLU.project(
            windowPoint,
            this._position,
            aView.getCamera().getViewProjectionMatrix(),
            [0, 0, aView.size[0], aView.size[1]]
        );
        windowPoint[0] = Math.round(windowPoint[0]);
        windowPoint[1] = Math.round(windowPoint[1]);
        windowPoint[1] = aView.size[1] - 1 - windowPoint[1];

        // get the model space position of the annotation for rounded window coordinates, because when reading the depth buffer,
        // the round is applied and then we do not compare the correct values
        let msPositionForRoundedWindowPoint = this._pgc.msPositionForRoundedWindowPoint;
        GLU.unProject(
            msPositionForRoundedWindowPoint,
            windowPoint[0],
            windowPoint[1],
            windowPoint[2],
            aView.getCamera().getViewProjectionMatrix(),
            [0, 0, aView.size[0], aView.size[1]]
        );

        // get depth of the corrected annotation position to rounded window coordinates
        GLU.project(
            windowPoint,
            msPositionForRoundedWindowPoint,
            aView.getCamera().getViewProjectionMatrix(),
            [0, 0, aView.size[0], aView.size[1]]
        );

        // read depth buffer value
        let points = this._renderDocument.renderForValues(aView, {
            type: "cdwk_depth",
            readPosition: [Math.round(windowPoint[0]), Math.round(windowPoint[1])]
        });

        if (points[0]) {
            let distA = vec3.distance(aView.getCamera().eye, msPositionForRoundedWindowPoint);
            let distB = vec3.distance(aView.getCamera().eye, points[0]);

            if (distA === distB) {
                // cadwork.webgl.Debug.logInfo("same");
            }
            if (distA > distB && Math.abs(distA - distB) > 1.0) {
                occluded = true;
            }

            // test if the annotation 3d point is in a camera positive half space (not behind the camera plane)
            let cam2PointVec = vec3.create();
            vec3.subtract(cam2PointVec, this._position, aView.getCamera().eye);
            visible = vec3.dot(cam2PointVec, aView.getCamera().getViewVector()) > 0.0;
        }

        if (this._callback === undefined || !this._callback) {
            return false;
        }

        // transform from physical coordinates to css screen coordinates
        this._cssScreenPosition = this._sceneAPI.getCanvas().getPageMousePositionFromPhysPosition([windowPoint[0], windowPoint[1]]);
        this._callback.call(this, this._cssScreenPosition[0], this._cssScreenPosition[1], occluded, visible);

        return true;
    }
}
